/**
 * Pixel-art battery screen for the 26×26 face grid: outline + nub, fill bar, lightning bolt and a % readout.
 * Returns the cell's look: 'off' | 'dim' | 'lit' | 'hot'.
 */
export type CellLook = 'off' | 'lit' | 'hot';

export interface BatteryState {
  level: number;
  impulse: number;
  plugged: boolean;
  time: number;
}

const BODY = { c0: 3, c1: 20, r0: 5, r1: 14 };
const NUB = { c0: 21, c1: 22, r0: 8, r1: 11 };
const FILL = { c0: 5, c1: 18, r0: 7, r1: 12 };
const BOLT = ['...##.', '..##..', '.#####', '...##.', '..##..', '.##...'];
const BOLT_C0 = 9;
const DIGITS_R0 = 17;

const GLYPHS: Record<string, string[]> = {
  '0': ['111', '101', '101', '101', '111'],
  '1': ['010', '110', '010', '010', '111'],
  '2': ['111', '001', '111', '100', '111'],
  '3': ['111', '001', '111', '001', '111'],
  '4': ['101', '101', '111', '001', '001'],
  '5': ['111', '100', '111', '001', '111'],
  '6': ['111', '100', '111', '101', '111'],
  '7': ['111', '001', '010', '010', '010'],
  '8': ['111', '101', '111', '101', '111'],
  '9': ['111', '101', '111', '001', '111'],
  '%': ['101', '001', '010', '100', '101'],
};

export function batteryCell(i: number, j: number, grid: number, s: BatteryState): CellLook {
  // Designed on a 26 grid; center it on any grid
  const o = Math.floor((grid - 26) / 2);
  const c = i - o;
  const r = j - o;
  const low = s.level < 0.25;

  // Outline (blinks when low, flashes on each click)
  const inBody = c >= BODY.c0 && c <= BODY.c1 && r >= BODY.r0 && r <= BODY.r1;
  const onEdge = inBody && (c === BODY.c0 || c === BODY.c1 || r === BODY.r0 || r === BODY.r1);
  const onNub = c >= NUB.c0 && c <= NUB.c1 && r >= NUB.r0 && r <= NUB.r1;
  if (onEdge || onNub) {
    if (s.impulse > 0.4) return 'hot';
    if (low && !s.plugged && Math.sin(s.time * 10) < 0) return 'off';
    return 'lit';
  }

  // Fill bar + lightning bolt (bolt is cut out of the fill, lit where empty)
  if (c >= FILL.c0 && c <= FILL.c1 && r >= FILL.r0 && r <= FILL.r1) {
    const cols = FILL.c1 - FILL.c0 + 1;
    const filled = Math.max(1, Math.round(s.level * cols));
    const isFill = c - FILL.c0 < filled;
    const br = r - FILL.r0;
    const bc = c - BOLT_C0;
    const isBolt = s.plugged && br >= 0 && br < BOLT.length && bc >= 0 && bc < 6 && BOLT[br][bc] === '#';
    const lit = isBolt ? !isFill : isFill;
    if (!lit) return 'off';
    // Leading edge shimmers while charging
    if (isFill && c - FILL.c0 === filled - 1 && s.plugged && Math.sin(s.time * 24 + r) > 0) return 'hot';
    return s.impulse > 0.6 && isFill ? 'hot' : 'lit';
  }

  // Percentage readout under the battery
  const text = `${Math.round(Math.min(1, s.level) * 100)}%`;
  const w = text.length * 4 - 1;
  const c0 = Math.floor((26 - w) / 2);
  const dr = r - DIGITS_R0;
  const dc = c - c0;
  if (dr >= 0 && dr < 5 && dc >= 0 && dc < w && dc % 4 !== 3) {
    const glyph = GLYPHS[text[Math.floor(dc / 4)]];
    if (glyph && glyph[dr][dc % 4] === '1') return 'lit';
  }
  return 'off';
}
